/**
 * Flow assignment dispatch queue heuristics (L2-UI-WH-01 · H-08)
 *
 * Shared server/client filter — matches flowId · tags · label against fulfilment /
 * warehouse / shipping markers (PAGE-AUDIT §Business Missing/Broken).
 */

export const FLOW_ASSIGNMENT_QUEUE_DISPATCH = 'dispatch' as const;

export type FlowAssignmentQueueType = typeof FLOW_ASSIGNMENT_QUEUE_DISPATCH;

const DISPATCH_MARKERS = [
  'dispatch',
  'fulfilment',
  'fulfillment',
  'warehouse',
  'warehouse_transfer',
  'shipping',
  'delivery',
  '출고',
  '배송',
  '납품',
] as const;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function matchesMarker(haystack: string): boolean {
  const normalized = normalize(haystack);
  return DISPATCH_MARKERS.some((marker) => normalized.includes(marker));
}

export interface DispatchQueueAssignmentLike {
  flowId: string;
  status?: string;
  tags?: string[];
  name?: string;
}

/** Active assignment that looks like warehouse / fulfilment / dispatch work */
export function isDispatchQueueAssignment(row: DispatchQueueAssignmentLike): boolean {
  if ((row.status ?? 'active') !== 'active') {
    return false;
  }
  if (matchesMarker(row.flowId)) {
    return true;
  }
  if (row.name && matchesMarker(row.name)) {
    return true;
  }
  if (row.tags?.some((tag) => matchesMarker(tag))) {
    return true;
  }
  return false;
}

export function filterDispatchQueueAssignments<T extends DispatchQueueAssignmentLike>(
  rows: T[]
): T[] {
  return rows.filter(isDispatchQueueAssignment);
}

export function resolveFlowAssignmentQueueType(
  value: string | null | undefined
): FlowAssignmentQueueType | null {
  if (value === FLOW_ASSIGNMENT_QUEUE_DISPATCH) {
    return FLOW_ASSIGNMENT_QUEUE_DISPATCH;
  }
  return null;
}
